/**
 * /table · BookingSheet — unified concierge-wrapped reservation UX.
 *
 * Founder ruling 5 Jun 2026 (locked):
 *   - Same sheet for every route. The MEMBER never feels handed off.
 *   - Submit → POST /api/table/concierge-request → lc_concierge_basket
 *     (single Zoho pipeline · ruling v2.1).
 *   - Instant path: the response carries `provider_link`. We surface
 *     a prominent "Complete your booking on {provider} ↗" button
 *     that opens in a NEW TAB so /table stays open.
 *   - Concierge path: full request flows to the desk · no provider
 *     button shown.
 *   - Toast: "Request secured · LC-XXXXX" — tap-feedback only ·
 *     NEVER says "confirmed" on instant.
 *   - The persistent whisper (W-I1 / W-C1) is dispatched by the
 *     backend via Gupshup. The same copy is also shown on the
 *     confirmation step for in-app continuity.
 *
 * Request-mode honesty (no live availability feed yet):
 *   - Time slots labelled "not yet confirmed · request slot".
 */
import React, { useEffect, useMemo, useState } from "react";
import { useEmbedMember } from "@/hooks/useEmbedMember";
import { conciergeRequest } from "@/lib/table-api";
import { ROUTE, providerEmbedUrl } from "./route-utils";
import { RouteChip } from "./route-primitives";

const LUNCH  = ["12:00", "12:30", "13:00", "13:30", "14:00"];
const DINNER = ["18:30", "19:00", "19:30", "20:00", "20:30", "21:00", "21:30"];

function next14() {
    const out = [];
    const fmt = { weekday: "short", day: "numeric", month: "short" };
    for (let i = 0; i < 14; i++) {
        const d = new Date();
        d.setDate(d.getDate() + i);
        const parts = new Intl.DateTimeFormat("en-GB", fmt).formatToParts(d);
        out.push({
            iso: d.toISOString().slice(0, 10),
            dow: i === 0 ? "Today" : parts.find((p) => p.type === "weekday").value,
            day: parts.find((p) => p.type === "day").value,
            mon: parts.find((p) => p.type === "month").value,
        });
    }
    return out;
}

// 90-day horizon · work-order v2 §3 binding. We default to a 14-day
// horizontal strip · the calendar control lets the member pick any
// date out to ~90 days. Min = today (no past dates) · max = +90 days.
function maxDateIso() {
    const d = new Date();
    d.setDate(d.getDate() + 90);
    return d.toISOString().slice(0, 10);
}
function todayIso() {
    return new Date().toISOString().slice(0, 10);
}
function fmtCalendarLabel(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso + "T00:00:00");
        return new Intl.DateTimeFormat("en-GB", {
            weekday: "short", day: "numeric", month: "short",
        }).format(d);
    } catch {
        return iso;
    }
}

const Icon = ({ d, size = 16, strokeWidth = 1.5 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={strokeWidth}
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {d}
    </svg>
);
const IconUsers   = (p) => <Icon {...p} d={<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>} />;
const IconCal     = (p) => <Icon {...p} d={<><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>} />;
const IconClock   = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>} />;
const IconClose   = (p) => <Icon {...p} d={<><path d="M18 6L6 18M6 6l12 12"/></>} strokeWidth={2} />;
const IconMinus   = (p) => <Icon {...p} d={<line x1="5" y1="12" x2="19" y2="12"/>} strokeWidth={2} />;
const IconPlus    = (p) => <Icon {...p} d={<><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>} strokeWidth={2} />;
const IconCheck   = (p) => <Icon {...p} d={<polyline points="20 6 9 17 4 12"/>} strokeWidth={2} />;

function Stepper({ onClick, children, disabled }) {
    return (
        <button
            type="button" onClick={onClick} disabled={disabled}
            style={{
                width: 34, height: 34, borderRadius: 17,
                border: "1px solid var(--t-line2)",
                background: "var(--t-paper)",
                color: "var(--t-ink)",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.4 : 1,
                display: "inline-flex", alignItems: "center",
                justifyContent: "center",
            }}
        >
            {children}
        </button>
    );
}

function Field({ icon: Ico, label, children }) {
    return (
        <div className="mb-5">
            <div className="flex items-center gap-2 mb-2">
                <Ico size={14} />
                <span
                    className="t-mono uppercase"
                    style={{
                        fontSize: 10, letterSpacing: "0.22em",
                        color: "var(--t-ink3)", fontWeight: 500,
                    }}
                >
                    {label}
                </span>
            </div>
            {children}
        </div>
    );
}

export function BookingSheet({ venue, route = "concierge", onClose, onConfirmed }) {
    const dates = useMemo(next14, []);
    const meta = ROUTE[route] || ROUTE.concierge;
    const hasProviderLink = route === "instant" || route === "google_reserve";

    // "Open ask" mode (founder ruling 27 Jun 2026 night): when the
    // member arrives via the /table hero "Ask the Concierge®" CTA,
    // there's no chosen venue yet. The sheet must collect the
    // restaurant name + city explicitly — it's a table-of-choice ask.
    const isOpenAsk = venue?.evidence_source === "table-hero-ask-concierge";

    const embedMember = useEmbedMember();
    const [covers, setCovers]             = useState(2);
    const [dateIso, setDateIso]           = useState(dates[0].iso);
    const [time, setTime]                 = useState(null);
    const [note, setNote]                 = useState("");
    const [name, setName]                 = useState(embedMember?.name  || "");
    const [email, setEmail]               = useState(embedMember?.email || "");
    const [phone, setPhone]               = useState(embedMember?.phone || "");
    const [submitting, setSubmitting]     = useState(false);
    const [done, setDone]                 = useState(null);
    const [error, setError]               = useState(null);
    // Open-ask only: editable restaurant + city. When isOpenAsk is
    // false these stay empty and the standard venue.name flow runs.
    const [askVenueName, setAskVenueName] = useState("");
    const [askVenueCity, setAskVenueCity] = useState("");

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onEsc = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", onEsc);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onEsc);
        };
    }, [onClose]);

    if (!venue) return null;

    const provider = venue.resolved_platform?.platform
        || (venue.booking_cta || "").replace(/^Reserve on\s+/i, "").trim()
        || null;
    const providerLink = venue.resolved_platform?.url || null;

    const confirm = async (e) => {
        e?.preventDefault?.();
        setError(null);
        // Open-ask gating: require restaurant name (city optional but
        // strongly recommended). Run BEFORE the standard validation
        // so the member sees the most-specific error first.
        if (isOpenAsk && !askVenueName.trim()) {
            setError("Which restaurant would you like? (Name + city helps us most.)");
            return;
        }
        if (!name.trim() || (!email.trim() && !phone.trim())) {
            setError("Please share your name and at least one contact (email or phone).");
            return;
        }
        if (!time) {
            setError("Please pick a preferred time.");
            return;
        }
        setSubmitting(true);
        try {
            // Resolve final venue identity. In open-ask mode we use
            // the member-typed values; otherwise the venue prop wins.
            const resolvedVenueName = isOpenAsk
                ? askVenueName.trim()
                : venue.name;
            const resolvedVenueCity = isOpenAsk
                ? (askVenueCity.trim() || null)
                : venue.city;
            const payload = {
                venue: {
                    name: resolvedVenueName,
                    city: resolvedVenueCity,
                    evidence_source: venue.evidence_source,
                },
                member_name: name.trim(),
                member_email: email.trim() || null,
                member_phone: phone.trim() || null,
                context: {
                    date: dateIso,
                    time,
                    party_size: Number(covers) || 2,
                    special_requests: note.trim() || null,
                    city: resolvedVenueCity,
                },
                resolver_output: null,
                route,
                provider,
                provider_link: providerLink,
                member_completing_url: providerLink,
            };
            const result = await conciergeRequest(payload);
            if (result?.ok) {
                const refUp = (result.member_reference || result.ticket_id || "")
                    .toUpperCase();
                setDone({
                    ref: refUp,
                    providerLink: result.provider_link || providerLink,
                    route: result.route || route,
                    state: result.state || "INTENT_LOGGED",
                });
                onConfirmed?.({ ...result, member_reference: refUp });
            } else {
                setError(result?.detail || "Could not submit. Please try again.");
            }
        } catch (err) {
            setError(err?.body || err?.message || "Network error. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const venueLocation = [venue.city, venue.country].filter(Boolean).join(", ");

    return (
        <div
            data-testid="booking-sheet"
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-6"
        >
            <button
                aria-label="Close"
                onClick={onClose}
                className="t-scrim absolute inset-0"
                style={{
                    background: "rgba(33, 28, 21, 0.55)",
                    backdropFilter: "blur(4px)",
                    border: 0, cursor: "default",
                }}
            />
            <div
                className="t-sheet relative w-full sm:max-w-md overflow-hidden flex flex-col"
                style={{
                    // Item 19 iOS polish: dynamic viewport units +
                    // safe-area-inset so the sheet doesn't get clipped
                    // by the iOS home-indicator bar. The maxHeight is
                    // also what makes the inner <form scrollY> WORK
                    // on desktop — without it the modal grows to full
                    // content height and pushes the close button off-
                    // screen above the viewport (founder ear-test
                    // 5 Jun 2026: "desktop stuck").
                    maxHeight: "min(92dvh, 92vh)",
                    paddingBottom: "env(safe-area-inset-bottom, 0px)",
                    background: "var(--t-paper2)",
                    borderRadius: 22,
                    boxShadow: "0 40px 100px rgba(33,28,21,0.32)",
                    border: "1px solid var(--t-line)",
                }}
            >
                {/* Concierge-wrapper header */}
                <div
                    className="px-6 pt-5 pb-4"
                    style={{ borderBottom: "1px solid var(--t-line)" }}
                >
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <span
                                className="t-mono uppercase"
                                style={{
                                    fontSize: 10, letterSpacing: "0.22em",
                                    color: "var(--t-brass)",
                                }}
                            >
                                reserve a table · by Concierge<sup
                                    style={{
                                        fontSize: "0.7em",
                                        marginLeft: "1px",
                                        fontWeight: "normal",
                                        verticalAlign: "super",
                                        lineHeight: 0,
                                    }}
                                    aria-label="registered trademark"
                                >®</sup>
                            </span>
                            <h3
                                className="t-serif"
                                style={{
                                    fontSize: 26, fontWeight: 500,
                                    lineHeight: 1.05, marginTop: 4,
                                    color: "var(--t-ink)",
                                    letterSpacing: "-0.01em",
                                }}
                                data-testid="booking-sheet-venue-name"
                            >
                                {isOpenAsk ? "Your table of choice" : venue.name}
                            </h3>
                            <p style={{ fontSize: 12.5, color: "var(--t-ink2)", marginTop: 2 }}>
                                {isOpenAsk
                                    ? "Tell us where — we'll do the rest."
                                    : venueLocation}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="booking-sheet-close"
                            aria-label="Close"
                            style={{
                                color: "var(--t-ink2)",
                                background: "transparent", border: 0,
                                cursor: "pointer", padding: 4,
                            }}
                        >
                            <IconClose size={20} />
                        </button>
                    </div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <RouteChip route={route} small />
                        <AltRoutesChooser
                            altRoutes={venue?.alt_routes}
                            currentVia={
                                venue?.resolved_platform?.platform || venue?.route_via
                            }
                            onSwitch={(alt) => {
                                window.open(
                                    alt.url,
                                    "_blank", "noopener,noreferrer",
                                );
                            }}
                        />
                    </div>
                </div>

                {!done ? (
                    <form
                        onSubmit={confirm}
                        className="px-6 py-5 overflow-y-auto"
                        style={{ flex: 1 }}
                    >
                        {/* Open-ask · the member is naming their table of
                           choice (no venue chosen upstream). Restaurant
                           name is required; city helps the desk a lot.
                           Hidden in the normal flow where the venue is
                           already known. */}
                        {isOpenAsk ? (
                            <>
                                <Field icon={IconUsers} label="Restaurant of choice">
                                    <input
                                        type="text"
                                        data-testid="booking-sheet-ask-venue-name"
                                        value={askVenueName}
                                        onChange={(e) => setAskVenueName(e.target.value)}
                                        placeholder="e.g. Bombay Canteen, Gymkhana London, Septime…"
                                        autoFocus
                                        className="w-full"
                                        style={{
                                            padding: "12px 14px",
                                            border: "1px solid var(--t-line2)",
                                            borderRadius: 12,
                                            background: "var(--t-paper)",
                                            color: "var(--t-ink)",
                                            fontSize: 15,
                                            fontFamily: "inherit",
                                            outline: "none",
                                            boxSizing: "border-box",
                                        }}
                                    />
                                </Field>
                                <Field icon={IconCal} label="City">
                                    <input
                                        type="text"
                                        data-testid="booking-sheet-ask-venue-city"
                                        value={askVenueCity}
                                        onChange={(e) => setAskVenueCity(e.target.value)}
                                        placeholder="e.g. Mumbai · London · Paris"
                                        className="w-full"
                                        style={{
                                            padding: "12px 14px",
                                            border: "1px solid var(--t-line2)",
                                            borderRadius: 12,
                                            background: "var(--t-paper)",
                                            color: "var(--t-ink)",
                                            fontSize: 15,
                                            fontFamily: "inherit",
                                            outline: "none",
                                            boxSizing: "border-box",
                                        }}
                                    />
                                </Field>
                            </>
                        ) : null}

                        <Field icon={IconUsers} label="Party">
                            <div className="flex items-center gap-3">
                                <Stepper
                                    onClick={() => setCovers((c) => Math.max(1, c - 1))}
                                    disabled={covers <= 1}
                                >
                                    <IconMinus size={14} />
                                </Stepper>
                                <span
                                    className="t-serif"
                                    data-testid="booking-sheet-covers"
                                    style={{
                                        fontSize: 22, minWidth: 90,
                                        textAlign: "center",
                                    }}
                                >
                                    {covers}{covers >= 12 ? "+" : ""}
                                    <span
                                        style={{
                                            fontSize: 13, color: "var(--t-ink2)",
                                            marginLeft: 6,
                                        }}
                                    >
                                        {covers === 1 ? "guest" : "guests"}
                                    </span>
                                </span>
                                <Stepper
                                    onClick={() => setCovers((c) => Math.min(40, c + 1))}
                                    disabled={covers >= 40}
                                >
                                    <IconPlus size={14} />
                                </Stepper>
                            </div>
                        </Field>

                        <Field icon={IconCal} label="Date">
                            <div
                                className="flex gap-1.5 overflow-x-auto pb-1"
                                style={{ scrollbarWidth: "thin" }}
                                data-testid="booking-sheet-date-strip"
                            >
                                {dates.map((d) => {
                                    const active = d.iso === dateIso;
                                    return (
                                        <button
                                            key={d.iso}
                                            type="button"
                                            onClick={() => { setDateIso(d.iso); setTime(null); }}
                                            data-testid={`booking-sheet-date-${d.iso}`}
                                            style={{
                                                flexShrink: 0,
                                                minWidth: 60, padding: "9px 8px",
                                                borderRadius: 12,
                                                border: `1px solid ${active ? "var(--t-ink)" : "var(--t-line2)"}`,
                                                background: active ? "var(--t-ink)" : "var(--t-card)",
                                                color: active ? "var(--t-paper2)" : "var(--t-ink)",
                                                cursor: "pointer",
                                                transition: "all 0.18s",
                                                textAlign: "center",
                                            }}
                                        >
                                            <div
                                                className="t-mono uppercase"
                                                style={{
                                                    fontSize: 9, letterSpacing: "0.12em",
                                                    opacity: active ? 0.7 : 0.55,
                                                }}
                                            >
                                                {d.dow}
                                            </div>
                                            <div
                                                className="t-serif"
                                                style={{ fontSize: 18, fontWeight: 500, lineHeight: 1 }}
                                            >
                                                {d.day}
                                            </div>
                                            <div
                                                className="t-mono"
                                                style={{ fontSize: 9, opacity: active ? 0.7 : 0.55 }}
                                            >
                                                {d.mon}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            {/* "More dates" · 90-day calendar picker
                               (work-order v2 §3 binding). The 14-day
                               strip is the quick-pick · the calendar
                               extends the horizon to +90 days. */}
                            <div
                                className="flex items-center gap-2 mt-2 flex-wrap"
                                data-testid="booking-sheet-90day"
                            >
                                <label
                                    className="t-mono uppercase"
                                    htmlFor="booking-sheet-date-input"
                                    style={{
                                        fontSize: 9, letterSpacing: "0.18em",
                                        color: "var(--t-ink3)",
                                    }}
                                >
                                    or pick a date up to 90 days out
                                </label>
                                <input
                                    id="booking-sheet-date-input"
                                    data-testid="booking-sheet-date-input"
                                    type="date"
                                    value={dateIso}
                                    min={todayIso()}
                                    max={maxDateIso()}
                                    onChange={(e) => {
                                        if (e.target.value) {
                                            setDateIso(e.target.value);
                                            setTime(null);
                                        }
                                    }}
                                    style={{
                                        background: "var(--t-paper)",
                                        border: "1px solid var(--t-line2)",
                                        borderRadius: 10,
                                        padding: "5px 10px",
                                        fontSize: 12,
                                        color: "var(--t-ink)",
                                        fontFamily: "'IBM Plex Mono', monospace",
                                        outline: "none",
                                        cursor: "pointer",
                                    }}
                                />
                                {!dates.some((d) => d.iso === dateIso) ? (
                                    <span
                                        data-testid="booking-sheet-date-extended-label"
                                        className="t-serif italic"
                                        style={{
                                            fontSize: 12, color: "var(--t-brass)",
                                        }}
                                    >
                                        {fmtCalendarLabel(dateIso)}
                                    </span>
                                ) : null}
                            </div>
                        </Field>

                        <Field icon={IconClock} label="Time · preferred">
                            <div
                                style={{ fontSize: 11, color: "var(--t-ink3)", marginBottom: 8 }}
                                data-testid="booking-sheet-time-honesty"
                            >
                                not yet confirmed · request slot
                            </div>
                            <SlotRow label="Lunch" slots={LUNCH} value={time} onPick={setTime} />
                            <SlotRow label="Dinner" slots={DINNER} value={time} onPick={setTime} />
                        </Field>

                        <div className="t-hairline my-4" />

                        <Field icon={IconUsers} label="Your name">
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Full name"
                                data-testid="booking-sheet-name"
                                style={inputStyle}
                            />
                        </Field>
                        <div className="grid sm:grid-cols-2 gap-x-3">
                            <Field icon={IconUsers} label="Email">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="name@domain.com"
                                    data-testid="booking-sheet-email"
                                    style={inputStyle}
                                />
                            </Field>
                            <Field icon={IconUsers} label="WhatsApp / phone">
                                <input
                                    type="tel"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="+91 …"
                                    data-testid="booking-sheet-phone"
                                    style={inputStyle}
                                />
                            </Field>
                        </div>

                        <Field icon={IconUsers} label="A note (optional)">
                            <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Birthday cake · terrace seating · dietary preferences…"
                                rows={2}
                                data-testid="booking-sheet-note"
                                style={{ ...inputStyle, height: 70, padding: "10px 14px" }}
                            />
                        </Field>

                        {error ? (
                            <div
                                data-testid="booking-sheet-error"
                                style={{
                                    marginTop: 8, fontSize: 13,
                                    color: "var(--t-pending)",
                                    background: "var(--t-pending-wash)",
                                    border: "1px solid var(--t-pending)",
                                    borderRadius: 12, padding: "10px 14px",
                                }}
                            >
                                {String(error)}
                            </div>
                        ) : null}

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="booking-sheet-submit"
                            style={{
                                marginTop: 18, width: "100%",
                                height: 50, padding: "0 22px",
                                borderRadius: 14,
                                background: hasProviderLink ? "var(--t-ink)" : "var(--t-brass)",
                                color: "var(--t-paper2)",
                                border: 0, cursor: submitting ? "not-allowed" : "pointer",
                                fontSize: 15, fontWeight: 600,
                                letterSpacing: "0.01em",
                                opacity: submitting ? 0.7 : 1,
                                display: "inline-flex", alignItems: "center",
                                justifyContent: "center", gap: 8,
                            }}
                        >
                            <span>{meta.icon}</span>
                            {submitting ? "Securing the request…" : meta.cta}
                        </button>
                        <p
                            style={{ marginTop: 10, fontSize: 11, color: "var(--t-ink3)", textAlign: "center" }}
                        >
                            Your request is logged with Les Concierges® · ticket reference
                            issued on submit · no card details collected here.
                        </p>
                    </form>
                ) : (
                    <BookingConfirmation
                        done={done}
                        venue={venue}
                        provider={provider}
                        date={dateIso}
                        seats={covers}
                        onClose={onClose}
                    />
                )}
            </div>
        </div>
    );
}

const inputStyle = {
    width: "100%", height: 44, padding: "0 14px",
    background: "var(--t-paper)",
    border: "1px solid var(--t-line2)",
    borderRadius: 12,
    color: "var(--t-ink)",
    fontSize: 15,
    fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
    outline: "none",
};

function SlotRow({ label, slots, value, onPick }) {
    return (
        <div style={{ marginBottom: 10 }}>
            <div
                style={{
                    fontSize: 11, color: "var(--t-ink3)",
                    marginBottom: 6, fontStyle: "italic",
                }}
            >
                {label}
            </div>
            <div className="flex flex-wrap gap-1.5">
                {slots.map((s) => {
                    const active = s === value;
                    return (
                        <button
                            key={s}
                            type="button"
                            onClick={() => onPick(s)}
                            data-testid={`booking-sheet-slot-${s.replace(":", "")}`}
                            style={{
                                padding: "6px 11px",
                                borderRadius: 20,
                                border: `1px solid ${active ? "var(--t-ink)" : "var(--t-line2)"}`,
                                background: active ? "var(--t-ink)" : "var(--t-card)",
                                color: active ? "var(--t-paper2)" : "var(--t-ink)",
                                fontSize: 13, cursor: "pointer",
                                transition: "all 0.16s",
                                fontFeatureSettings: '"tnum"',
                            }}
                        >
                            {s}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Confirmation step shown after the basket POST succeeds.
 * Surface contract (founder ruling):
 *   - LC-XXXXX reference prominent
 *   - Instant route: prominent provider button · NEW TAB · /table stays open
 *   - In-app whisper text matches the backend's W-I1 / W-C1 send
 *   - NO "confirmed" language on instant path
 *
 * Item 7b · Provider widget embed (4 Jun 2026 work-order follow-up):
 *   When the venue is Tier-1 Instant AND we can build an embeddable
 *   widget URL for the provider, render that widget inside the popup
 *   in an <iframe>. If the iframe fails to load (X-Frame-Options /
 *   CSP frame-ancestors / load-timeout) we MUST fall back to the
 *   existing "Complete on {provider} ↗" new-tab button. NEVER leave
 *   the member with a blank/broken iframe.
 *
 *   Honesty: copy stays as-is · the LC ticket + whisper fire on submit
 *   regardless of embed vs new-tab. We never imply /table itself
 *   confirmed the table.
 */
function BookingConfirmation({ done, venue, provider, date, seats, onClose }) {
    const ref = done.ref;
    // Doctrine 7 Jun 2026 · same predicate as the parent sheet · both
    // `instant` and `google_reserve` are provider-link routes; the
    // member needs the URL surfaced on confirmation, not the desk
    // handoff copy.
    const hasProviderLink = done.route === "instant" || done.route === "google_reserve";
    const inAppWhisper = hasProviderLink
        ? `Your request for ${venue.name} is with us (ref ${ref}). Complete your booking on ${provider || "the venue"} below — we'll flag you to the venue in parallel so they expect you.`
        : `Your request for ${venue.name} is with our team (ref ${ref}). A Concierge® will reach the restaurant directly and confirm back.`;

    // Compute the embed candidate · null when we can't honestly build one.
    const embed = hasProviderLink && done.providerLink
        ? providerEmbedUrl(provider, done.providerLink, { date, seats })
        : null;

    // Embed state machine: "try" → "ready" → "blocked"
    //   try     · iframe mounted but load not confirmed yet
    //   ready   · iframe load event fired AFTER a real network round
    //             trip · we trust the provider's own UX from here
    //   blocked · load timeout · onerror · OR the synthetic refusal
    //             tripwire (load fired suspiciously fast → almost
    //             certainly an X-Frame-Options refusal page rendered
    //             from cache) · we cleanly swap to the new-tab button
    const [embedState, setEmbedState] = useState(embed ? "try" : "blocked");
    const mountAtRef = React.useRef(0);

    useEffect(() => {
        if (!embed || embedState !== "try") return;
        mountAtRef.current = Date.now();
        // Cross-origin iframes don't fire reliable error events for
        // X-Frame-Options refusals — most browsers show a blank pane
        // and never report. So we time-bound the try: 5.5s without a
        // load event = blocked. (Real iframes load in ~1-2s.)
        const id = setTimeout(() => {
            setEmbedState((s) => (s === "try" ? "blocked" : s));
        }, 5500);
        return () => clearTimeout(id);
    }, [embed, embedState]);

    const handleIframeLoad = () => {
        // Synthetic refusal tripwire (4 Jun 2026 founder ear-test fix):
        // a genuine cross-origin load takes 400ms+. When `onload`
        // fires under 200ms after mount it almost always means the
        // browser served the synthetic "refused to connect" page
        // from its own UI · NOT the provider. Treat that as blocked.
        const elapsed = mountAtRef.current
            ? Date.now() - mountAtRef.current
            : 9999;
        if (elapsed < 250) {
            setEmbedState("blocked");
            return;
        }
        setEmbedState("ready");
    };

    const showEmbed = embed && embedState !== "blocked";
    const showFallbackButton = !showEmbed && hasProviderLink && done.providerLink;

    return (
        <div className="px-6 py-7 overflow-y-auto" data-testid="booking-confirmation">
            <div
                className="t-fade flex items-center justify-center"
                style={{
                    width: 52, height: 52, borderRadius: 30,
                    border: "1.5px solid var(--t-verified)",
                    background: "var(--t-verified-wash)",
                    color: "var(--t-verified)",
                    margin: "0 auto",
                }}
                aria-hidden="true"
            >
                <IconCheck size={22} strokeWidth={2.4} />
            </div>
            <h3
                className="t-serif text-center"
                style={{
                    fontSize: 26, fontWeight: 500, color: "var(--t-ink)",
                    marginTop: 16, lineHeight: 1.1,
                    letterSpacing: "-0.01em",
                }}
            >
                Request secured
            </h3>
            <p
                className="t-mono text-center"
                data-testid="booking-confirmation-ref"
                style={{
                    fontSize: 14, color: "var(--t-brass)",
                    letterSpacing: "0.18em", marginTop: 4,
                }}
            >
                {ref}
            </p>
            <p
                style={{
                    fontSize: 14, color: "var(--t-ink2)",
                    textAlign: "center", lineHeight: 1.55,
                    marginTop: 14,
                }}
                data-testid="booking-confirmation-whisper"
            >
                {inAppWhisper}
            </p>

            {/* ── Item 7b · Embedded provider widget */}
            {showEmbed ? (
                <div
                    className="t-fade"
                    data-testid="booking-confirmation-embed"
                    style={{
                        marginTop: 18,
                        borderRadius: 14,
                        overflow: "hidden",
                        border: "1px solid var(--t-line2)",
                        background: "var(--t-paper)",
                        position: "relative",
                    }}
                >
                    <div
                        className="t-mono uppercase"
                        style={{
                            fontSize: 9.5,
                            letterSpacing: "0.18em",
                            color: "var(--t-ink3)",
                            padding: "9px 14px",
                            borderBottom: "1px solid var(--t-line2)",
                            background: "var(--t-paper2)",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <span>completing on {embed.provider}</span>
                        <a
                            href={done.providerLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="booking-confirmation-embed-new-tab"
                            style={{
                                color: "var(--t-brass)",
                                textDecoration: "none",
                                fontWeight: 600,
                            }}
                        >
                            open in new tab ↗
                        </a>
                    </div>
                    <iframe
                        src={embed.url}
                        title={`Complete your reservation on ${embed.provider}`}
                        data-testid="booking-confirmation-embed-iframe"
                        onLoad={handleIframeLoad}
                        onError={() => setEmbedState("blocked")}
                        style={{
                            width: "100%",
                            height: 460,
                            border: 0,
                            display: "block",
                            background: "var(--t-paper)",
                        }}
                        sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
                        referrerPolicy="no-referrer-when-downgrade"
                    />
                </div>
            ) : null}

            {showFallbackButton ? (
                <a
                    href={done.providerLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="booking-confirmation-provider-link"
                    aria-label={`Complete your booking on ${provider || "the venue"} · opens in a new tab`}
                    className="t-fade"
                    style={{
                        display: "block", textAlign: "center",
                        marginTop: 20,
                        background: "var(--t-ink)", color: "var(--t-paper2)",
                        borderRadius: 14,
                        padding: "13px 22px 14px",
                        fontSize: 15, fontWeight: 600,
                        letterSpacing: "0.01em",
                        textDecoration: "none",
                        lineHeight: 1.25,
                    }}
                >
                    Complete your booking on {provider || "the venue"} ↗
                    {/*
                      Founder doctrine 7 Jun 2026 (post-Lisbon ear-test):
                      claim only what's true · same principle as the cover
                      banner. The button hands off to a new tab when the
                      provider refuses framing (SevenRooms per-venue,
                      TableCheck universally, mobile/iOS short-circuit) —
                      SAY so plainly rather than relying on the ↗ glyph.
                    */}
                    <span
                        data-testid="booking-confirmation-provider-link-newtab-hint"
                        style={{
                            display: "block",
                            fontSize: 11,
                            fontWeight: 500,
                            letterSpacing: "0.04em",
                            marginTop: 3,
                            opacity: 0.78,
                        }}
                    >
                        opens in a new tab · /table stays right here
                    </span>
                </a>
            ) : null}

            <p
                style={{
                    fontSize: 11, color: "var(--t-ink3)",
                    marginTop: 14, textAlign: "center",
                }}
            >
                A WhatsApp note from us is on its way too · /table stays open
                in case you want to return.
            </p>

            <button
                type="button"
                onClick={onClose}
                data-testid="booking-confirmation-close"
                style={{
                    marginTop: 18, width: "100%",
                    height: 44, padding: "0 22px", borderRadius: 12,
                    background: "var(--t-paper)",
                    color: "var(--t-ink2)",
                    border: "1px solid var(--t-line2)",
                    fontSize: 13, fontWeight: 500, cursor: "pointer",
                }}
            >
                Done
            </button>
        </div>
    );
}



// ── Item 21e · multi-route choice ─────────────────────────────────
//
// Founder ear-test 5 Jun 2026: "When a venue has 2+ verified booking
// routes (Onrique = Zomato + Google Reserve), offer both as a choice.
// Only ever verified routes, never an invented third."
//
// The primary route stays the headline. Each alt is a small pill ·
// label says "or via {platform}" so the member knows it's the same
// booking, different provider. Clicking opens the alt provider in a
// new tab (Google Reserve + TableCheck both block iframing, the
// inline embed flow doesn't apply here).

function AltRoutesChooser({ altRoutes, currentVia, onSwitch }) {
    const visible = (altRoutes || []).filter(
        (a) => a && a.url && a.platform && a.platform !== currentVia,
    );
    if (visible.length === 0) return null;
    return (
        <div
            data-testid="booking-alt-routes"
            className="flex items-center gap-1 flex-wrap"
            style={{ marginLeft: 4 }}
        >
            <span
                className="t-mono uppercase"
                style={{
                    fontSize: 9, letterSpacing: "0.18em",
                    color: "var(--t-ink3)",
                }}
            >
                or via
            </span>
            {visible.map((a) => (
                <button
                    key={a.platform}
                    type="button"
                    data-testid={`booking-alt-route-${a.platform.toLowerCase().replace(/\W+/g, "-")}`}
                    onClick={() => onSwitch && onSwitch(a)}
                    aria-label={`Open this booking on ${a.platform} · opens in a new tab`}
                    title={`Opens ${a.platform} in a new tab`}
                    style={{
                        background: "var(--t-card)",
                        color: "var(--t-ink2)",
                        border: "1px solid var(--t-line2)",
                        borderRadius: 999,
                        padding: "3px 11px",
                        fontSize: 11, fontWeight: 600,
                        letterSpacing: "0.02em",
                        cursor: "pointer",
                    }}
                >
                    {a.platform} ↗
                </button>
            ))}
        </div>
    );
}
